/**
 * Utilitaires pour les formations
 * Fonctions pures <20 lignes chacune
 */

import { libelleFormat, venduParSessions } from './formuleFormation.js'

export const categories = [
  'INCENDIE',
  'SST',
  'AFGSU',
  'PSC SECOURISME',
  'HABILITATION ELECTRIQUE',
  'GESTES ET POSTURES',
  'HACCP',
  'CSE CHSCT',
  'TRAVAIL EN HAUTEUR',
  'TERRORISTE PPMS',
  'CACES',
  'SSIAP CQPAPS',
  'AIPR',
  'CATEC',
  'ATEX',
  'RPS QUALITÉ DE VIE AU TRAVAIL',
  'RISQUE CHIMIQUE'
]

export const formationsRequiringExteriorSpace = [
  'Formation EPI Equipier de Première Intervention - MEX et évacuation',
  'Formation incendie manipulation des extincteurs conforme au code du travail',
  'Formation manipulation des extincteurs',
  'Formation PTI Premier Témoin Incendie',
  'Formation incendie crèche',
  'Formation SSI Système de Sécurité Incendie',
  'Safety day icendie',
  'Formation initiale ARI Appareil Respiratoire Isolant (ARI)',
  'Formation incendie ERP type J ou U (EHPAD/Hôpital) (SSI, transfert horizontal)',
  'Formation incendie établissement type O & R (internat, hôtel) (SSI)'
]

export const ARI_FORMATION_NAME = 'Formation initiale ARI Appareil Respiratoire Isolant (ARI)'
export const CAMION_UNITE_MOBILE_FORMATION_NAME = 'Formation camion en unité mobile'

export function requiresExteriorSpace(formationName) {
  if (!formationName) return false
  return formationsRequiringExteriorSpace.some(name => 
    formationName.trim().toLowerCase() === name.trim().toLowerCase()
  )
}

export function isARIFormation(formationName) {
  if (!formationName) return false
  return formationName.trim().toLowerCase() === ARI_FORMATION_NAME.trim().toLowerCase()
}

export function isCamionUniteMobileFormation(formationName) {
  if (!formationName) return false
  return formationName.trim().toLowerCase() === CAMION_UNITE_MOBILE_FORMATION_NAME.trim().toLowerCase()
}

export function formatSessionDate(dateString) {
  if (!dateString) return ''
  try {
    const parts = dateString.trim().split('/')
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10) - 1
      const year = parseInt(parts[2], 10)
      
      if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
        const date = new Date(year, month, day)
        return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      }
    }
    const date = new Date(dateString)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    }
    return dateString
  } catch {
    return dateString
  }
}

/**
 * Intitulé d'une formation dans une liste de choix.
 *
 * La durée y figurait entre parenthèses pour distinguer les intitulés en
 * double, déclinés en plusieurs durées. Ce n'est plus son rôle : une formation
 * porte désormais ses durées dans sa grille de formules, et le devis en choisit
 * une après avoir choisi la formation. Les quelques doublons qui subsistent au
 * catalogue ont vocation à fusionner en une formation à plusieurs formules.
 *
 * La sélection, elle, n'a jamais reposé sur l'intitulé : c'est l'identifiant
 * catalogue qui est transporté.
 */
export function formatFormationName(formation) {
  if (!formation) return ''
  if (typeof formation === 'string') return formation

  return formation.NOM || ''
}

/**
 * Durée écrite court, pour un libellé : 420 → « 7h », 90 → « 1h30 »,
 * 30 → « 30min ».
 *
 * Le catalogue écrit « 7H00 » ; sur un formulaire public c'est du jargon.
 */
export function libelleDureeCourt(minutes) {
  if (!Number.isFinite(minutes) || minutes <= 0) return ''

  const heures = Math.floor(minutes / 60)
  const reste = minutes % 60
  if (!heures) return `${reste}min`
  return reste ? `${heures}h${String(reste).padStart(2, '0')}` : `${heures}h`
}

/**
 * Ce que dure une formation, telle qu'on l'annonce à côté de son nom :
 * « 7h » quand elle ne se vend qu'ainsi, « 1 à 7h » quand elle se décline.
 *
 * Les deux bornes partagent leur unité quand elles sont en heures rondes
 * — « 1 à 7h » se lit mieux que « 1h à 7h ». Une formation sans formule
 * retombe sur sa durée historique, qui est déjà du texte.
 */
export function plageDurees(formation) {
  // Par sessions, la durée se dit en formats : « 1/2 journée ou 1 journée ».
  if (venduParSessions(formation?.FORMULES)) {
    const formats = [...new Set(formation.FORMULES.map((f) => f.dureeMinutes))].sort((a, b) => a - b)
    return formats.map(libelleFormat).join(' ou ')
  }

  const minutes = [...new Set((formation?.FORMULES || []).map((f) => f.dureeMinutes))]
    .filter((m) => Number.isFinite(m) && m > 0)
    .sort((a, b) => a - b)

  if (!minutes.length) return formation?.DUREE || ''

  const court = libelleDureeCourt(minutes[0])
  if (minutes.length === 1) return court

  const dernier = minutes[minutes.length - 1]
  const rondes = minutes[0] % 60 === 0 && dernier % 60 === 0
  return rondes
    ? `${minutes[0] / 60} à ${dernier / 60}h`
    : `${court} à ${libelleDureeCourt(dernier)}`
}

/** Le nom d'une formation suivi de ce qu'elle dure : « SST (14h) ». */
export function nomAvecDuree(formation) {
  const nom = formatFormationName(formation)
  const plage = plageDurees(formation)
  return plage ? `${nom} (${plage})` : nom
}

/**
 * Les durées qu'une formation propose, pour une liste déroulante.
 * Vide quand elle n'en a qu'une : il n'y a alors rien à choisir.
 */
export function choixDeDuree(formation) {
  const minutes = [...new Set((formation?.FORMULES || []).map((f) => f.dureeMinutes))]
    .filter((m) => Number.isFinite(m) && m > 0)
    .sort((a, b) => a - b)

  if (minutes.length < 2) return []
  return minutes.map((m) => ({ value: m, label: libelleDureeCourt(m) }))
}

